import { useProfiles } from '@api/hooks/useProfiles';
import type { ButtonProps } from '@gurupay/gp-ui-library';
import { Button, Typography } from '@gurupay/gp-ui-library';
import { useAuth } from '@hooks/index';
import clsx from 'clsx';
import type { KeyboardEvent, KeyboardEventHandler, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';

const RETRY_SECONDS = 120;
const CODE_LENGTH = 6;

interface TfaSigningProps {
	buttons?: ReactNode;
	errorSignal?: number;
	isConfirmDisabled?: boolean;
	isRequestDisabled?: boolean;
	notification?: ReactNode;
	onConfirm: (code: string) => void;
	onCountdownEnd?: () => void;
	requestOtp: () => Promise<unknown>;
	submitButtonProps: ButtonProps;
}

const TfaSigning = ({
	buttons,
	errorSignal,
	isConfirmDisabled = false,
	isRequestDisabled = false,
	notification,
	onConfirm,
	onCountdownEnd,
	requestOtp,
	submitButtonProps,
}: TfaSigningProps) => {
	const { t } = useTranslation('', { keyPrefix: 'tfa' });
	const profile = useProfiles();
	const { session } = useAuth();
	const otpInputRef = useRef<HTMLDivElement>(null);
	const hasNotifiedCountdownEnd = useRef(false);
	const [otp, setOtp] = useState('');
	const [secondsLeft, setSecondsLeft] = useState(RETRY_SECONDS);
	const [hasRequestedOtp, setHasRequestedOtp] = useState(false);
	const isSms = profile.data?.secondFactorMethod === 'sms';
	const isOtpActive = hasRequestedOtp && secondsLeft > 0;

	useEffect(() => {
		if (!hasRequestedOtp || !otpInputRef.current) return;

		otpInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
		const firstInput = otpInputRef.current.querySelector('input');
		firstInput?.focus();
	}, [hasRequestedOtp]);

	useEffect(() => {
		if (!isOtpActive) return;

		const interval = window.setInterval(() => {
			setSecondsLeft((currentSeconds) => Math.max(currentSeconds - 1, 0));
		}, 1000);

		return () => window.clearInterval(interval);
	}, [isOtpActive]);

	useEffect(() => {
		if (!secondsLeft || hasNotifiedCountdownEnd.current) return;

		hasNotifiedCountdownEnd.current = true;
		setOtp('');
		onCountdownEnd?.();
	}, [onCountdownEnd, secondsLeft]);

	useEffect(() => {
		if (!errorSignal) return;

		setOtp('');
		otpInputRef.current?.querySelector('input')?.focus();
	}, [errorSignal]);

	const handleRequestOtp = () => {
		requestOtp()
			.then(() => {
				hasNotifiedCountdownEnd.current = false;
				setOtp('');
				setHasRequestedOtp(true);
				setSecondsLeft(RETRY_SECONDS);
			})
			.catch(() => undefined);
	};

	const handleKeyPress =
		(libraryOnKeyDown: KeyboardEventHandler<HTMLInputElement>) =>
		(event: KeyboardEvent<HTMLInputElement>) => {
			libraryOnKeyDown(event);
			if (
				event.key !== 'Enter' ||
				otp?.length !== CODE_LENGTH ||
				!isOtpActive ||
				isConfirmDisabled
			) {
				return;
			}

			onConfirm(otp);
		};

	return (
		<div className="flex flex-col gap-y-space-24">
			<div>
				<Typography level="h4">{isSms ? t('phone') : t('email')}</Typography>
				<Typography level="text">
					{isSms ? session?.identity?.traits.phone : session?.identity?.traits.email}
				</Typography>
			</div>

			{hasRequestedOtp && isOtpActive && (
				<fieldset>
					<legend className="text-xl-bold mb-space-16 block">{t('pin')}</legend>

					<div ref={otpInputRef} className="flex flex-col" id="pinInputWithLoader">
						<OtpInput
							inputStyle={{
								maxWidth: '56px',
								width: '100%',
								height: '56px',
								textAlign: 'center',
							}}
							inputType="number"
							numInputs={CODE_LENGTH}
							onChange={setOtp}
							renderInput={(renderInputProps) => (
								<input
									{...renderInputProps}
									className="border border-solid border-neutral-200 mr-space-8 w-14 h-14 rounded-lg hover:border-neutral-400 focus-visible:border-neutral-950 outline-none u-focus-ring"
									onKeyDown={handleKeyPress(renderInputProps.onKeyDown)}
								/>
							)}
							value={otp}
						/>

						<Typography className="mt-space-8" level="small-text">
							{isSms
								? t('retry', { time: secondsLeft })
								: t('retryEmail', { time: secondsLeft })}
						</Typography>
					</div>
				</fieldset>
			)}

			<div className="flex flex-col sm:flex-row items-center flex-wrap gap-space-16">
				{isOtpActive ? (
					<Button
						variant="primary"
						{...submitButtonProps}
						className={clsx('w-full sm:w-max', submitButtonProps.className)}
						disabled={otp?.length !== CODE_LENGTH || isConfirmDisabled}
						onClick={() => onConfirm(otp)}
					/>
				) : (
					<Button
						className="w-full sm:w-max"
						disabled={isRequestDisabled}
						onClick={handleRequestOtp}
						variant="primary"
					>
						{hasRequestedOtp
							? isSms
								? t('buttons.resend')
								: t('buttons.resendEmail')
							: isSms
								? t('buttons.send')
								: t('buttons.sendEmail')}
					</Button>
				)}
				{buttons}
			</div>

			{notification}
		</div>
	);
};

export { TfaSigning };
